import axios from 'axios';
import { getApiUrl, getBackendUrl } from '../utils/apiClient';

// Use dynamic API URL
const API_BASE_URL = getApiUrl();

export interface ReceiptItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface Transaction {
  id?: number;
  timestamp?: string;
  items: ReceiptItem[];
  subtotal: number;
  tax: number;
  total: number;
  payment_type: 'cash' | 'paypay';
  received?: number;
  change?: number;
  order_type: 'eatin' | 'takeout';
}

export interface PrintResult {
  success: boolean;
  error?: string;
  pdfBackup?: {
    fileName: string;
    filePath: string;
  };
}

export interface PDFResult {
  success: boolean;
  fileName?: string;
  filePath?: string;
  error?: string;
}

export interface PrinterStatus {
  success: boolean;
  status: 'online' | 'offline' | 'timeout';
  printerIP?: string;
  printerPort?: number;
  error?: string;
}

class ReceiptService {
  // レシート印刷
  async printReceipt(transaction: Transaction, printerType: string = 'network', printerModel: string = 'star'): Promise<PrintResult> {
    try {
      const response = await axios.post(`${API_BASE_URL}/receipt/print`, {
        transaction,
        printerType,
        printerModel
      });
      
      // サーバーからclaimIdが返された場合、トランザクションに追加
      if (response.data.claimId) {
        transaction.claimId = response.data.claimId;
        console.log('ClaimId received from server:', response.data.claimId);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Print receipt error:', error);
      // バックエンドでHTMLバックアップが作成された場合はそれを返す（エラーとしない）
      if (error.response?.data?.htmlBackup) {
        return {
          success: false,
          error: error.response?.data?.error,
          pdfBackup: error.response.data.htmlBackup
        };
      }
      // レスポンスのデータを確認
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error(error.response?.data?.error || 'Failed to print receipt');
    }
  }

  // PDF生成
  async generatePDF(transaction: Transaction): Promise<PDFResult> {
    try {
      const response = await axios.post(`${API_BASE_URL}/receipt/generate-pdf`, {
        transaction
      });
      return response.data;
    } catch (error: any) {
      console.error('Generate PDF error:', error);
      throw new Error(error.response?.data?.error || 'Failed to generate PDF');
    }
  }

  // PDFダウンロード
  async downloadPDF(fileName: string): Promise<void> {
    try {
      const response = await axios.get(`${API_BASE_URL}/receipt/download/${fileName}`, {
        responseType: 'blob'
      });
      
      // ブラウザでダウンロード
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download PDF error:', error);
      throw new Error(error.response?.data?.error || 'Failed to download PDF');
    }
  }

  // プリンターステータス確認
  async checkPrinterStatus(printerModel: string = 'star', ip?: string, port?: string, printerType: string = 'windows'): Promise<PrinterStatus> {
    try {
      const params = new URLSearchParams({
        printerModel,
        printerType,
        ...(ip && { ip }),
        ...(port && { port })
      });
      const response = await axios.get(`${API_BASE_URL}/receipt/printer-status?${params}`);
      return response.data;
    } catch (error: any) {
      console.error('Check printer status error:', error);
      return {
        success: false,
        status: 'offline',
        error: error.message
      };
    }
  }

  // テスト印刷（プレゼンテーションサンプル）
  async testPrint(printerType: string = 'network', printerModel: string = 'star', ip?: string, port?: string): Promise<PrintResult> {
    try {
      const response = await axios.post(`${API_BASE_URL}/receipt/test-print`, {
        printerType,
        printerModel,
        ip,
        port
      });
      
      // プレゼンテーションサンプルHTMLが生成された場合
      if (response.data.htmlUrl) {
        // 新しいウィンドウでHTMLを開いて印刷
        const printWindow = window.open(`${getBackendUrl()}${response.data.htmlUrl}`, '_blank');
        
        if (printWindow) {
          // HTMLが読み込まれた後に印刷ダイアログを表示
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
              // 印刷後にウィンドウを閉じる（オプション）
              // setTimeout(() => printWindow.close(), 1000);
            }, 500);
          };
        }
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Test print error:', error);
      throw new Error(error.response?.data?.error || 'Failed to test print');
    }
  }

  // レシート再印刷
  async reprintReceipt(transactionId: number, printerType: string = 'network'): Promise<PrintResult> {
    try {
      const response = await axios.post(`${API_BASE_URL}/receipt/reprint/${transactionId}`, {
        printerType
      });
      return response.data;
    } catch (error: any) {
      console.error('Reprint receipt error:', error);
      throw new Error(error.response?.data?.error || 'Failed to reprint receipt');
    }
  }

  // レシート印刷（自動フォールバック付き）
  async printWithFallback(transaction: Transaction): Promise<PrintResult> {
    // プリンター設定を取得
    const settings = localStorage.getItem('printerSettings');
    // ESC/POSモードならネットワーク印刷、スターラインモードならWindows印刷
    const printerConfig = settings ? JSON.parse(settings) : { type: 'network', model: 'star' };
    
    // 設定に基づいて印刷を試みる
    const result = await this.printReceipt(transaction, printerConfig.type || 'network', printerConfig.model || 'star');
    
    if (result.success) {
      return result;
    }
    
    // 失敗した場合、バックエンドでHTMLバックアップが作成されているか確認
    if (result.pdfBackup || result.htmlBackup) {
      const backup = result.pdfBackup || result.htmlBackup;
      // バックエンドで既にHTMLが生成されている場合、それを使用
      return {
        success: true,  // HTMLバックアップも成功として扱う
        method: 'html',
        message: 'プリンター接続エラー。HTMLとして保存しました。',
        fileName: backup.fileName,
        filePath: backup.filePath
      };
    }
    
    // エラーメッセージを含む結果を返す
    return {
      success: false,
      error: result.error || 'プリンター接続エラー',
      message: 'プリンター接続エラー。レシート印刷に失敗しました。'
    };
  }

  // ローカルストレージにレシートデータを保存（オフライン対応）
  saveReceiptToLocal(transaction: Transaction): void {
    try {
      const receipts = this.getLocalReceipts();
      receipts.push({
        ...transaction,
        savedAt: new Date().toISOString(),
        printed: false
      });
      
      localStorage.setItem('pending_receipts', JSON.stringify(receipts));
    } catch (error) {
      console.error('Save receipt to local error:', error);
    }
  }

  // ローカルストレージからレシートデータを取得
  getLocalReceipts(): any[] {
    try {
      const data = localStorage.getItem('pending_receipts');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Get local receipts error:', error);
      return [];
    }
  }

  // ローカルストレージのレシートデータをクリア
  clearLocalReceipts(): void {
    try {
      localStorage.removeItem('pending_receipts');
    } catch (error) {
      console.error('Clear local receipts error:', error);
    }
  }

  // オフライン時の保留レシートを印刷
  async printPendingReceipts(): Promise<void> {
    const pendingReceipts = this.getLocalReceipts();
    
    if (pendingReceipts.length === 0) {
      return;
    }
    
    const printedReceipts: any[] = [];
    const failedReceipts: any[] = [];
    
    for (const receipt of pendingReceipts) {
      try {
        const result = await this.printReceipt(receipt, 'network');
        if (result.success) {
          printedReceipts.push(receipt);
        } else {
          failedReceipts.push(receipt);
        }
      } catch (error) {
        failedReceipts.push(receipt);
      }
    }
    
    // 失敗したレシートのみローカルに保持
    if (failedReceipts.length > 0) {
      localStorage.setItem('pending_receipts', JSON.stringify(failedReceipts));
    } else {
      this.clearLocalReceipts();
    }
    
    console.log(`Printed ${printedReceipts.length} receipts, ${failedReceipts.length} failed`);
  }
}

const receiptService = new ReceiptService();

export default receiptService;
export type { Transaction, PrintResult, PDFResult, PrinterStatus };